import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import {
  ArrowLeft, Award, CheckCircle2, ChevronDown, ChevronRight,
  Clock, FileText, Layers, ShieldCheck, Sparkles, Zap,
  ExternalLink, LogIn, ListChecks, Binary, BrainCircuit,
  Activity, Pill, Microscope, Timer, Share2, HelpCircle,
  GraduationCap, Building2, Stethoscope, BookOpen, AlertCircle
} from 'lucide-react';
import { StorageService } from '../services/storageService';
import { CertificationType, CertificationGatewaySeo } from '../types';
import { getDefaultGatewaySeo } from '../services/promptService';
import { useAuth } from '../components/AuthBridge';
import { useThemeTokens } from '../hooks/useActiveTheme';

const DEFAULT_PROGRAM_IMAGES: Record<string, string> = {
  cna: 'https://images.unsplash.com/photo-1576765608535-5f04d1e3f289?auto=format&fit=crop&w=1200&q=80',
  phlebotomy: 'https://images.unsplash.com/photo-1579154204601-01588f351e67?auto=format&fit=crop&w=1200&q=80',
  medical_assistant: 'https://images.unsplash.com/photo-1629909613654-28e377c37b09?auto=format&fit=crop&w=1200&q=80',
  pharmacy_tech: 'https://images.unsplash.com/photo-1587854692152-cbe660dbde88?auto=format&fit=crop&w=1200&q=80',
  pct: 'https://images.unsplash.com/photo-1538108149393-fbbd81895907?auto=format&fit=crop&w=1200&q=80'
};

const DEFAULT_PROGRAM_DESCRIPTIONS: Record<string, string> = {
  cna: 'Comprehensive state-test simulations, 22 procedural skills checklists, and clinical rationales verified across all 50 state testing registries.',
  phlebotomy: 'Venipuncture clinical protocols, order of draw, specimen handling, infection prevention, and high-yield NHA/AMCA exam banks.',
  medical_assistant: 'Multi-skilled outpatient clinic exam room preparation, vital signs mastery, pharmacology basics, and administrative CCMA/RMA board questions.',
  pharmacy_tech: 'Pharmaceutical dosage calculations, sterile compounding protocols, federal drug regulations, and PTCE board exam simulations.',
  pct: 'Acute hospital inpatient care, telemetry and EKG lead placement fundamentals, advanced hemodynamics, and CPCT board preparation.'
};

/**
 * Synchronously retrieves initial program details data from:
 * 1. window.__INITIAL_PROGRAM_DATA__ or DOM script tag (injected by server-side SSR)
 * 2. LocalStorage cached certifications
 * 3. Immediate fallback from promptService defaults
 *
 * This ensures ZERO hydration flicker, NO loading spinner flash, and instant paint matching the SSR HTML.
 */
function getInitialProgramData(targetId: string): { cert: CertificationType; seo: CertificationGatewaySeo } {
  const normId = (targetId || 'cna').toLowerCase();
  const defaultImg = DEFAULT_PROGRAM_IMAGES[normId] || DEFAULT_PROGRAM_IMAGES.cna;
  const defaultDesc = DEFAULT_PROGRAM_DESCRIPTIONS[normId] || '';

  if (typeof window !== 'undefined') {
    // 1. Check window.__INITIAL_PROGRAM_DATA__ or DOM script element
    try {
      let data = (window as any).__INITIAL_PROGRAM_DATA__;
      if (!data) {
        const scriptEl = document.getElementById('__INITIAL_PROGRAM_DATA__');
        if (scriptEl && scriptEl.textContent) {
          data = JSON.parse(scriptEl.textContent);
        }
      }
      if (data && (!normId || data.id?.toLowerCase() === normId)) {
        const hydratedCert: CertificationType = {
          id: data.id || normId,
          name: data.name,
          shortName: data.shortName,
          gatewayCategory: data.gatewayCategory,
          gatewayDescription: data.gatewayDescription || defaultDesc,
          gatewayImageUrl: data.gatewayImageUrl || defaultImg,
          categories: ['Clinical Knowledge', 'Patient Care', 'Safety & Infection Control'],
          gatewaySeo: {
            pageTitle: data.pageTitle,
            metaDescription: data.metaDescription,
            heroHeadline: data.heroHeadline,
            heroSubheadline: data.heroSubheadline,
            roleOverview: data.roleOverview,
            whoIsThisFor: data.whoIsThisFor,
            keyResponsibilities: data.keyResponsibilities,
            examFormatSummary: data.examFormatSummary,
            domains: data.domains,
            clinicalSkills: data.clinicalSkills,
            testingVendors: data.testingVendors,
            platformBenefits: data.platformBenefits,
            faqs: data.faqs,
            careerOpportunities: data.careerOpportunities,
            studyTips: data.studyTips,
            keywords: data.keywords,
            isPublished: true
          }
        };
        return { cert: hydratedCert, seo: hydratedCert.gatewaySeo! };
      }
    } catch (e) {
      console.warn('Could not read __INITIAL_PROGRAM_DATA__:', e);
    }

    // 2. Check cached certifications in localStorage
    try {
      const cached = StorageService.getCachedCertifications();
      const found = cached.find(c => c.id.toLowerCase() === normId);
      if (found) {
        const seo = found.gatewaySeo && found.gatewaySeo.roleOverview
          ? found.gatewaySeo
          : getDefaultGatewaySeo(found.id, found.name, found.shortName, found.gatewayCategory);
        return {
          cert: {
            ...found,
            gatewayImageUrl: (found.gatewayImageUrl && found.gatewayImageUrl.trim()) || defaultImg,
            gatewayDescription: (found.gatewayDescription && found.gatewayDescription.trim()) || defaultDesc,
            gatewaySeo: seo
          },
          seo
        };
      }
    } catch (e) {
      // ignore
    }
  }

  // 3. Fallback to default gateway SEO
  const defaultSeo = getDefaultGatewaySeo(normId);
  const certName = defaultSeo.pageTitle?.split(' (')[0] || normId.toUpperCase();
  const isAlliedHealth = normId === 'phlebotomy' || normId === 'medical_assistant' || normId === 'pharmacy_tech';
  const defaultCert: CertificationType = {
    id: normId,
    name: certName,
    shortName: normId.toUpperCase(),
    gatewayCategory: isAlliedHealth ? 'AlliedHealth' : 'Nursing',
    gatewayImageUrl: defaultImg,
    gatewayDescription: defaultDesc,
    categories: ['Clinical Knowledge', 'Patient Care', 'Safety & Infection Control'],
    gatewaySeo: defaultSeo
  };
  return { cert: defaultCert, seo: defaultSeo };
}

export const ProgramDetailsPage: React.FC = () => {
  const { certId: routeCertId } = useParams<{ certId?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { activeCert, switchCert, isAuthenticated } = useAuth();
  const tokens = useThemeTokens();

  // Extract candidate certification id from params or pathname
  const targetId = useMemo(() => {
    if (routeCertId) return routeCertId.toLowerCase();
    const parts = location.pathname.split('/').filter(Boolean);
    if (parts[0] === 'programs' || parts[0] === 'program' || parts[0] === 'certifications') {
      return (parts[1] || 'cna').toLowerCase();
    }
    if (parts[0] === 'practice-exams') {
      return (parts[2] || parts[1] || 'cna').toLowerCase();
    }
    if (parts[0] === 'practice') {
      return (parts[1] || 'cna').toLowerCase();
    }
    return 'cna';
  }, [routeCertId, location.pathname]);

  // Synchronous initial state guarantees ZERO hydration flicker
  const initialData = useMemo(() => getInitialProgramData(targetId), [targetId]);
  const [cert, setCert] = useState<CertificationType>(initialData.cert);
  const [seo, setSeo] = useState<CertificationGatewaySeo>(initialData.seo);

  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const [copiedLink, setCopiedLink] = useState(false);
  const [activeNavSection, setActiveNavSection] = useState<'overview' | 'blueprint' | 'skills' | 'offerings' | 'careers' | 'faqs'>('overview');

  // Extract typography font-family, weight, and tracking from tokens.headerFont without conflicting text-color classes
  const headerFontFamily = useMemo(() => {
    return (tokens.headerFont || '')
      .split(' ')
      .filter(cls => !cls.startsWith('text-') && !cls.startsWith('dark:text-'))
      .join(' ');
  }, [tokens.headerFont]);

  // Non-blocking background sync with certification database
  useEffect(() => {
    const freshInitial = getInitialProgramData(targetId);
    setCert(freshInitial.cert);
    setSeo(freshInitial.seo);

    let isMounted = true;
    const syncCert = async () => {
      try {
        const certs = await StorageService.getCertifications();
        if (!isMounted) return;
        const found = certs.find(c => c.id.toLowerCase() === targetId);
        if (found) {
          const hasCustomSeo = Boolean(
            found.gatewaySeo && 
            found.gatewaySeo.roleOverview && 
            found.gatewaySeo.roleOverview.trim().length > 20
          );
          setCert(prev => ({
            ...prev,
            ...found,
            gatewayImageUrl: (found.gatewayImageUrl && found.gatewayImageUrl.trim()) || prev.gatewayImageUrl || DEFAULT_PROGRAM_IMAGES[targetId] || DEFAULT_PROGRAM_IMAGES.cna,
            gatewayDescription: (found.gatewayDescription && found.gatewayDescription.trim()) || prev.gatewayDescription || DEFAULT_PROGRAM_DESCRIPTIONS[targetId] || '',
            gatewaySeo: hasCustomSeo ? found.gatewaySeo : prev.gatewaySeo
          }));
          if (hasCustomSeo && found.gatewaySeo) {
            setSeo(found.gatewaySeo);
          }
        }
      } catch (err) {
        console.warn('Background certification sync notice:', err);
      }
    };
    syncCert();
    return () => { isMounted = false; };
  }, [targetId]);

  // Dynamic SEO meta tags and Schema.org injection
  useEffect(() => {
    const pageTitle = seo.pageTitle || `${cert.name} (${cert.shortName || 'CERT'}) Certification Guide & Exam Prep 2026`;
    const metaDesc = seo.metaDescription || `Complete guide to ${cert.name} certification. Exam format, daily duties, skills checklists, and free practice tests.`;

    document.title = `${pageTitle} | Diamond School of Nursing`;

    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.setAttribute('name', 'description');
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDesc);

    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', pageTitle);
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute('content', metaDesc);

    let canonicalTag = document.querySelector('link[rel="canonical"]');
    if (!canonicalTag) {
      canonicalTag = document.createElement('link');
      canonicalTag.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalTag);
    }
    canonicalTag.setAttribute('href', `https://study.diamondnursingschool.com/programs/${cert.id}`);

    // Schema.org JSON-LD structured data injection
    const existingSchemaScript = document.getElementById('program-details-jsonld');
    if (existingSchemaScript) {
      existingSchemaScript.remove();
    }

    const jsonLdData: any = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "EducationalOccupationalCredential",
          "@id": `https://study.diamondnursingschool.com/programs/${cert.id}`,
          "name": `${cert.name} (${cert.shortName}) Board Certification`,
          "description": seo.roleOverview || metaDesc,
          "credentialCategory": "State Board Certification",
          "recognizedBy": {
            "@type": "EducationalOrganization",
            "name": "Diamond School of Nursing",
            "url": "https://study.diamondnursingschool.com/"
          }
        },
        {
          "@type": "Course",
          "name": `${cert.name} Board Exam Preparation & Practice Labs`,
          "description": metaDesc,
          "provider": {
            "@type": "EducationalOrganization",
            "name": "Diamond School of Nursing",
            "sameAs": "https://www.diamondnursingschool.com/"
          },
          "isAccessibleForFree": true
        }
      ]
    };

    if (seo.faqs && seo.faqs.length > 0) {
      jsonLdData["@graph"].push({
        "@type": "FAQPage",
        "mainEntity": seo.faqs.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      });
    }

    const script = document.createElement('script');
    script.id = 'program-details-jsonld';
    script.type = 'application/ld+json';
    script.text = JSON.stringify(jsonLdData);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById('program-details-jsonld');
      if (el) el.remove();
    };
  }, [cert, seo]);

  const handleStartPractice = async () => {
    if (cert && activeCert?.id !== cert.id) {
      try {
        await switchCert(cert.id);
      } catch (err) {
        console.warn('Auto switch cert notice:', err);
      }
    }
    navigate(`/practice/${cert.id}/practice`);
  };

  const handleEnterDashboard = async () => {
    if (cert && activeCert?.id !== cert.id) {
      try {
        await switchCert(cert.id);
      } catch (err) {
        console.warn('Auto switch cert notice:', err);
      }
    }
    navigate(`/practice/${cert.id}/dashboard`);
  };

  const handleRegisterLogin = () => {
    const currentUrl = window.location.href;
    const loginRedirectUrl = `https://www.diamondnursingschool.com/studyhub-login?redirect=${encodeURIComponent(currentUrl)}`;
    window.location.href = loginRedirectUrl;
  };

  const handleShare = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2500);
    }
  };

  const scrollToSection = (sectionId: 'overview' | 'blueprint' | 'skills' | 'offerings' | 'careers' | 'faqs') => {
    setActiveNavSection(sectionId);
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const categoryLabel = cert.gatewayCategory === 'AlliedHealth' ? 'Medical Tech & Clinical Lab' : 'Nursing & Patient Care';
  const defaultImg = DEFAULT_PROGRAM_IMAGES[cert.id?.toLowerCase()] || DEFAULT_PROGRAM_IMAGES[targetId] || DEFAULT_PROGRAM_IMAGES.cna;
  const coverImage = cert.gatewayImageUrl || defaultImg;
  const hasSkills = Boolean(seo.clinicalSkills && seo.clinicalSkills.length > 0);

  return (
    <div id="certification-gateway-details" className="space-y-10 sm:space-y-12 pb-16">
      {/* Top Breadcrumb & Share Bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 pb-4">
        <div className="flex items-center gap-2 text-xs font-bold text-slate-500">
          <a
            href="/gateway"
            className="hover:text-slate-800 transition-colors flex items-center gap-1"
          >
            <ArrowLeft size={14} /> All Certifications
          </a>
          <ChevronRight size={14} className="text-slate-300" />
          <span className="text-slate-700">{categoryLabel}</span>
          <ChevronRight size={14} className="text-slate-300" />
          <span className="text-slate-900 font-extrabold">{cert.shortName || cert.name} Guide</span>
        </div>

        <div className="flex items-center gap-2">
          <span className={`px-3 py-1 text-[10px] font-black uppercase tracking-widest ${tokens.badge}`}>
            ● Verified Board Standards
          </span>
          <button
            type="button"
            onClick={handleShare}
            className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer"
            title="Share this certification guide"
          >
            <Share2 size={13} />
            <span>{copiedLink ? 'Link Copied!' : 'Share'}</span>
          </button>
        </div>
      </div>

      {/* Main Hero Showcase */}
      <header className="relative rounded-3xl overflow-hidden border border-slate-200/90 shadow-sm bg-slate-950 text-white">
        <div className="relative min-h-[360px] sm:min-h-[420px] flex flex-col justify-end p-6 sm:p-10 lg:p-12">
          {/* Background Photo with Lighter, High-Visibility Overlay */}
          <div className="absolute inset-0 z-0">
            <img
              src={coverImage}
              alt={cert.name}
              className="w-full h-full object-cover object-center opacity-95 sm:opacity-100 transition-opacity duration-700"
              onError={(e) => {
                (e.target as HTMLImageElement).src = DEFAULT_PROGRAM_IMAGES.cna;
              }}
            />
            {/* Lighter, luminous gradient allowing hero imagery to be clearly visible without being pitch black */}
            <div className="absolute inset-0 bg-gradient-to-r from-slate-950/70 via-slate-950/35 to-transparent"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-transparent"></div>
          </div>

          {/* Hero Content */}
          <div className="relative z-10 space-y-5 max-w-4xl">
            <div className="flex flex-wrap items-center gap-2">
              <span className="px-3 py-1 bg-white/15 backdrop-blur-md rounded-full text-[10px] font-black uppercase tracking-widest text-sky-300 border border-white/10">
                {categoryLabel}
              </span>
              <span className="px-3 py-1 bg-emerald-500/20 backdrop-blur-md rounded-full text-[10px] font-black uppercase tracking-widest text-emerald-300 border border-emerald-500/30">
                100% Free Guest Mode
              </span>
            </div>

            <h1 className={`text-2xl sm:text-4xl lg:text-5xl font-black !text-white tracking-tight leading-tight drop-shadow-md ${headerFontFamily}`}>
              {seo.heroHeadline || cert.name}
            </h1>

            <p className="text-sm sm:text-base !text-slate-100 font-medium leading-relaxed max-w-3xl drop-shadow-sm">
              {seo.heroSubheadline || seo.metaDescription || cert.gatewayDescription}
            </p>

            {/* Quick Metrics Ribbon */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
              <div className="p-3.5 bg-white/10 backdrop-blur-md rounded-2xl border border-white/15">
                <p className="text-[10px] font-bold !text-slate-300 uppercase tracking-wider">Exam Questions</p>
                <p className="text-base sm:text-lg font-black !text-white">{seo.examFormatSummary?.totalQuestions || '60–100 Questions'}</p>
              </div>
              <div className="p-3.5 bg-white/10 backdrop-blur-md rounded-2xl border border-white/15">
                <p className="text-[10px] font-bold !text-slate-300 uppercase tracking-wider">Time Limit</p>
                <p className="text-base sm:text-lg font-black !text-white">{seo.examFormatSummary?.timeAllotted || '90–120 Minutes'}</p>
              </div>
              <div className="p-3.5 bg-white/10 backdrop-blur-md rounded-2xl border border-white/15">
                <p className="text-[10px] font-bold !text-slate-300 uppercase tracking-wider">Passing Target</p>
                <p className="text-base sm:text-lg font-black !text-emerald-300">{seo.examFormatSummary?.passingScore || '70% – 80%'}</p>
              </div>
              <div className="p-3.5 bg-white/10 backdrop-blur-md rounded-2xl border border-white/15">
                <p className="text-[10px] font-bold !text-slate-300 uppercase tracking-wider">Access Tier</p>
                <p className="text-base sm:text-lg font-black !text-sky-300">Free Instant Access</p>
              </div>
            </div>

            {/* Primary Action Buttons */}
            <div className="flex flex-wrap items-center gap-3 pt-3">
              <button
                type="button"
                onClick={handleStartPractice}
                className={`${tokens.primaryBtn} px-6 py-4 text-xs uppercase tracking-wider flex items-center gap-2 shadow-lg hover:shadow-xl cursor-pointer`}
              >
                <Zap size={16} />
                <span>Start Free Guest Practice</span>
              </button>

              <button
                type="button"
                onClick={handleEnterDashboard}
                className={`${tokens.secondaryBtn} px-6 py-4 text-xs uppercase tracking-wider flex items-center gap-2 cursor-pointer`}
              >
                <BookOpen size={16} />
                <span>Enter Study Hub</span>
              </button>

              {!isAuthenticated && (
                <button
                  type="button"
                  onClick={handleRegisterLogin}
                  className="px-5 py-4 bg-white/10 hover:bg-white/20 text-white rounded-xl text-xs font-bold transition-all flex items-center gap-2 border border-white/20 backdrop-blur-sm cursor-pointer"
                >
                  <LogIn size={15} />
                  <span>Register / Sign in</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Sticky Pills Bar */}
      <nav aria-label="Page Sections" className="sticky top-0 z-20 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md p-2 rounded-2xl border border-slate-200/80 shadow-xs flex items-center gap-1 sm:gap-2 overflow-x-auto scrollbar-none">
        <button
          type="button"
          onClick={() => scrollToSection('overview')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
            activeNavSection === 'overview' ? tokens.activeTab : tokens.inactiveTab
          }`}
        >
          Role Overview
        </button>
        <button
          type="button"
          onClick={() => scrollToSection('blueprint')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
            activeNavSection === 'blueprint' ? tokens.activeTab : tokens.inactiveTab
          }`}
        >
          Exam Blueprint
        </button>
        {hasSkills && (
          <button
            type="button"
            onClick={() => scrollToSection('skills')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
              activeNavSection === 'skills' ? tokens.activeTab : tokens.inactiveTab
            }`}
          >
            Skills Lab
          </button>
        )}
        <button
          type="button"
          onClick={() => scrollToSection('offerings')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
            activeNavSection === 'offerings' ? tokens.activeTab : tokens.inactiveTab
          }`}
        >
          Platform Offerings
        </button>
        <button
          type="button"
          onClick={() => scrollToSection('careers')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
            activeNavSection === 'careers' ? tokens.activeTab : tokens.inactiveTab
          }`}
        >
          Work Settings
        </button>
        <button
          type="button"
          onClick={() => scrollToSection('faqs')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
            activeNavSection === 'faqs' ? tokens.activeTab : tokens.inactiveTab
          }`}
        >
          FAQs ({seo.faqs?.length || 0})
        </button>
      </nav>

      {/* SECTION 1: ROLE OVERVIEW & SCOPE */}
      <section id="overview" className="p-8 sm:p-10 bg-white border border-slate-200 rounded-3xl shadow-xs space-y-6 scroll-mt-20">
        <div className="flex items-center gap-3">
          <div className={`p-3 rounded-2xl ${tokens.iconBg}`}>
            <GraduationCap size={22} />
          </div>
          <div>
            <h2 className={`text-xl sm:text-2xl font-black text-slate-900 ${tokens.headerFont}`}>
              What is a {cert.name}?
            </h2>
            <p className="text-xs text-slate-500 font-semibold">
              Healthcare career overview, clinical scope of practice, and professional expectations
            </p>
          </div>
        </div>

        <div className="prose prose-slate max-w-none text-slate-700 text-sm sm:text-base leading-relaxed space-y-4">
          <p>{seo.roleOverview}</p>
        </div>

        {/* Who is this for */}
        {seo.whoIsThisFor && seo.whoIsThisFor.length > 0 && (
          <div className="pt-4 border-t border-slate-100 space-y-4">
            <h3 className="text-xs font-black uppercase tracking-wider text-slate-500">
              Who This Certification Program Is For
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {seo.whoIsThisFor.map((item, idx) => (
                <div key={idx} className="p-4 bg-slate-50 rounded-2xl border border-slate-200/80 flex items-start gap-3">
                  <CheckCircle2 size={16} className="text-emerald-600 mt-0.5 shrink-0" />
                  <span className="text-xs font-bold text-slate-800 leading-snug">{item}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Key Clinical Duties */}
        {seo.keyResponsibilities && seo.keyResponsibilities.length > 0 && (
          <div className="pt-6 border-t border-slate-100 space-y-4">
            <h3 className="text-xs font-black uppercase tracking-wider text-slate-500">
              Core Clinical Responsibilities & Daily Scope
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {seo.keyResponsibilities.map((resp, idx) => (
                <div key={idx} className="p-4 bg-white border border-slate-200 rounded-2xl flex items-start gap-3 shadow-2xs">
                  <span className="w-5 h-5 rounded-full bg-slate-100 text-slate-700 font-black text-[10px] flex items-center justify-center shrink-0 mt-0.5">
                    {idx + 1}
                  </span>
                  <span className="text-xs font-medium text-slate-700 leading-relaxed">{resp}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* SECTION 2: OFFICIAL EXAMINATION FORMAT & BLUEPRINT */}
      <section id="blueprint" className="p-8 sm:p-10 bg-white border border-slate-200 rounded-3xl shadow-xs space-y-6 scroll-mt-20">
        <div className="flex items-center gap-3">
          <div className={`p-3 rounded-2xl ${tokens.iconBg}`}>
            <FileText size={22} />
          </div>
          <div>
            <h2 className={`text-xl sm:text-2xl font-black text-slate-900 ${tokens.headerFont}`}>
              Official Examination Format & Scoring Blueprint
            </h2>
            <p className="text-xs text-slate-500 font-semibold">
              Standardized test specifications, domain weight distributions, and passing thresholds
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-5 bg-slate-50 rounded-2xl border border-slate-200 space-y-1">
            <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Test Format</p>
            <p className="text-sm font-black text-slate-900">{seo.examFormatSummary?.formatType || 'Multiple-Choice CBT'}</p>
          </div>
          <div className="p-5 bg-slate-50 rounded-2xl border border-slate-200 space-y-1">
            <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Question Count & Time</p>
            <p className="text-sm font-black text-slate-900">{seo.examFormatSummary?.totalQuestions} ({seo.examFormatSummary?.timeAllotted})</p>
          </div>
          <div className="p-5 bg-slate-50 rounded-2xl border border-slate-200 space-y-1">
            <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Passing Threshold</p>
            <p className="text-sm font-black text-emerald-700">{seo.examFormatSummary?.passingScore || '70% – 80% Scaled Score'}</p>
          </div>
        </div>

        {seo.examFormatSummary?.skillsTestDescription && (
          <div className="p-5 bg-amber-50/70 border border-amber-200/80 rounded-2xl space-y-2">
            <div className="flex items-center gap-2 text-amber-900 font-black text-xs uppercase tracking-wider">
              <ListChecks size={16} className="text-amber-700" />
              <span>Clinical Skills Demonstration Component</span>
            </div>
            <p className="text-xs text-amber-950/90 font-medium leading-relaxed">
              {seo.examFormatSummary.skillsTestDescription}
            </p>
          </div>
        )}

        {/* Domain Breakdown Table */}
        {seo.domains && seo.domains.length > 0 && (
          <div className="pt-4 border-t border-slate-100 space-y-3">
            <h3 className="text-xs font-black uppercase tracking-wider text-slate-500">
              Exam Knowledge Domains & Weight Distribution
            </h3>
            <div className="overflow-x-auto rounded-2xl border border-slate-200">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="p-3.5 font-black text-slate-700 uppercase tracking-wider">Knowledge Domain</th>
                    <th className="p-3.5 font-black text-slate-700 uppercase tracking-wider">Weight</th>
                    <th className="p-3.5 font-black text-slate-700 uppercase tracking-wider">Questions</th>
                    <th className="p-3.5 font-black text-slate-700 uppercase tracking-wider">Key Competencies Evaluated</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {seo.domains.map((domain, idx) => (
                    <tr key={idx} className="hover:bg-slate-50/60 transition-colors">
                      <td className="p-3.5 font-bold text-slate-900 whitespace-nowrap">{domain.name}</td>
                      <td className="p-3.5 font-black text-sky-600 whitespace-nowrap">{domain.weight}</td>
                      <td className="p-3.5 font-semibold text-slate-600 whitespace-nowrap">{domain.questions || 'N/A'}</td>
                      <td className="p-3.5 text-slate-600 leading-relaxed min-w-[280px]">{domain.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Testing Vendors */}
        {seo.testingVendors && seo.testingVendors.length > 0 && (
          <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-black uppercase tracking-wider text-slate-500">Recognized Testing Vendors:</span>
            {seo.testingVendors.map((vendor, idx) => (
              <span key={idx} className="px-3 py-1 bg-white border border-slate-200 rounded-full text-xs font-bold text-slate-800 shadow-2xs">
                {vendor}
              </span>
            ))}
          </div>
        )}
      </section>

      {/* SECTION 3: CLINICAL SKILLS LAB CHECKLIST (When applicable) */}
      {hasSkills && (
        <section id="skills" className="p-8 sm:p-10 bg-amber-50/60 border border-amber-200/80 rounded-3xl shadow-xs space-y-6 scroll-mt-20">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-2xl bg-amber-100 text-amber-800">
                <ListChecks size={22} />
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-black text-amber-950">
                  High-Yield Clinical Skills Demonstration Checklist
                </h2>
                <p className="text-xs text-amber-800 font-semibold">
                  Critical step protocols evaluated during clinical demonstration exams
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate(`/practice/${cert.id}/skills`)}
              className="px-5 py-2.5 bg-amber-900 hover:bg-amber-800 text-white rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 whitespace-nowrap cursor-pointer shadow-sm"
            >
              <Activity size={14} /> Open Interactive Skills Lab
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-2">
            {(seo.clinicalSkills || []).map((skill, idx) => (
              <div key={idx} className="p-5 bg-white border border-amber-200/90 rounded-2xl space-y-2 shadow-2xs">
                <div className="flex items-start gap-2.5">
                  <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-900 font-black text-xs flex items-center justify-center shrink-0 mt-0.5">
                    {idx + 1}
                  </span>
                  <h4 className="text-sm font-black text-amber-950 leading-snug">{skill.name}</h4>
                </div>
                <p className="text-xs text-amber-950/80 leading-relaxed font-normal pt-1">
                  <strong className="text-amber-900 font-bold">Critical Step Protocol:</strong> {skill.criticalSteps}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* SECTION 4: PLATFORM ADVANTAGES & OFFERINGS */}
      <section id="offerings" className="p-8 sm:p-10 bg-slate-900 text-white rounded-3xl shadow-sm space-y-6 scroll-mt-20">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <span className="text-[10px] font-black uppercase tracking-widest text-sky-400">Diamond School Advantage</span>
            <h2 className={`text-xl sm:text-2xl font-black !text-white ${headerFontFamily}`}>
              What Our Study Hub Offers for Guaranteed First-Time Board Success
            </h2>
            <p className="text-xs !text-slate-300 font-medium mt-1">
              Doctoral-curated study resources designed to build high-stakes exam confidence
            </p>
          </div>
          <button
            type="button"
            onClick={handleStartPractice}
            className={`${tokens.primaryBtn} px-5 py-3 text-xs uppercase tracking-wider flex items-center gap-2 whitespace-nowrap cursor-pointer`}
          >
            <Zap size={14} /> Try Free Now
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-4">
          {(seo.platformBenefits || []).map((benefit, idx) => (
            <div key={idx} className="p-5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl transition-all space-y-2">
              <div className="flex items-center gap-2">
                <span className="w-7 h-7 rounded-xl bg-white/15 text-sky-300 flex items-center justify-center font-black text-xs">
                  {idx + 1}
                </span>
                <h4 className="text-sm font-black !text-white">{benefit.title}</h4>
              </div>
              <p className="text-xs !text-slate-300 leading-relaxed font-normal">{benefit.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SECTION 5: CLINICAL WORK ENVIRONMENTS & STUDY TIPS */}
      <section id="careers" className="grid grid-cols-1 md:grid-cols-2 gap-6 scroll-mt-20">
        {/* Career Opportunities */}
        <div className="p-8 bg-white border border-slate-200 rounded-3xl shadow-xs space-y-5">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-slate-100 text-slate-800 rounded-xl">
              <Building2 size={20} />
            </div>
            <div>
              <h3 className={`text-lg font-black text-slate-900 ${tokens.headerFont}`}>
                Clinical Work Environments
              </h3>
              <p className="text-xs text-slate-500 font-medium">Where certified {cert.name} professionals practice</p>
            </div>
          </div>

          <div className="space-y-2.5">
            {(seo.careerOpportunities || []).map((opp, idx) => (
              <div key={idx} className="p-3.5 bg-slate-50 rounded-xl border border-slate-200/80 flex items-center gap-3">
                <Stethoscope size={15} className="text-slate-500 shrink-0" />
                <span className="text-xs font-bold text-slate-800">{opp}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Study Tips */}
        <div className="p-8 bg-white border border-slate-200 rounded-3xl shadow-xs space-y-5">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-slate-100 text-slate-800 rounded-xl">
              <Sparkles size={20} />
            </div>
            <div>
              <h3 className={`text-lg font-black text-slate-900 ${tokens.headerFont}`}>
                Faculty Study Strategies
              </h3>
              <p className="text-xs text-slate-500 font-medium">Evidence-based test preparation advice</p>
            </div>
          </div>

          <div className="space-y-3">
            {(seo.studyTips || []).map((tip, idx) => (
              <div key={idx} className="p-3.5 bg-slate-50 rounded-xl border border-slate-200/80 space-y-1">
                <p className="text-xs font-bold text-slate-800 leading-relaxed">{tip}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 6: FREQUENTLY ASKED QUESTIONS (FAQ) */}
      <section id="faqs" className="p-8 sm:p-10 bg-white border border-slate-200 rounded-3xl shadow-xs space-y-6 scroll-mt-20">
        <div className="flex items-center gap-3">
          <div className={`p-3 rounded-2xl ${tokens.iconBg}`}>
            <HelpCircle size={22} />
          </div>
          <div>
            <h2 className={`text-xl sm:text-2xl font-black text-slate-900 ${tokens.headerFont}`}>
              Frequently Asked Questions (FAQ)
            </h2>
            <p className="text-xs text-slate-500 font-semibold">
              Common questions about the {cert.name} certification program and testing process
            </p>
          </div>
        </div>

        <div className="space-y-3">
          {(seo.faqs || []).map((faq, idx) => {
            const isOpen = openFaqIndex === idx;
            return (
              <div
                key={idx}
                className="border border-slate-200 rounded-2xl overflow-hidden transition-all duration-200"
              >
                <button
                  type="button"
                  onClick={() => setOpenFaqIndex(isOpen ? null : idx)}
                  className="w-full p-5 text-left bg-slate-50/70 hover:bg-slate-100 flex items-center justify-between gap-4 transition-colors cursor-pointer"
                >
                  <span className="text-xs sm:text-sm font-bold text-slate-800">{faq.question}</span>
                  <ChevronDown
                    size={16}
                    className={`text-slate-400 transition-transform duration-300 shrink-0 ${isOpen ? 'rotate-180' : ''}`}
                  />
                </button>
                {isOpen && (
                  <div className="p-5 bg-white text-xs sm:text-sm text-slate-600 font-medium leading-relaxed border-t border-slate-100">
                    {faq.answer}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Final Conversion Callout Card */}
      <footer className={`p-8 sm:p-12 text-center rounded-3xl shadow-md space-y-6 ${tokens.heroBannerBg}`}>
        <div className="max-w-2xl mx-auto space-y-3">
          <h3 className={`text-2xl sm:text-3xl font-black !text-white ${headerFontFamily}`}>
            Ready to Begin Your Healthcare Career?
          </h3>
          <p className="text-xs sm:text-sm !text-slate-100 font-medium leading-relaxed">
            Experience our accredited board-aligned question bank and interactive skills simulations. No credit card or registration required to start.
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
          <button
            type="button"
            onClick={handleStartPractice}
            className="px-8 py-4 bg-white text-slate-900 hover:bg-slate-100 rounded-xl font-black text-xs uppercase tracking-wider shadow-lg hover:shadow-xl transition-all flex items-center gap-2 active:scale-95 cursor-pointer"
          >
            <Zap size={16} className="text-amber-500" />
            <span>Start Free Guest Diagnostic</span>
          </button>

          <button
            type="button"
            onClick={handleEnterDashboard}
            className="px-8 py-4 bg-white/20 hover:bg-white/30 text-white rounded-xl font-black text-xs uppercase tracking-wider border border-white/30 transition-all flex items-center gap-2 cursor-pointer"
          >
            <BookOpen size={16} />
            <span>Candidate Dashboard</span>
          </button>

          {!isAuthenticated && (
            <button
              type="button"
              onClick={handleRegisterLogin}
              className="px-8 py-4 bg-white/10 hover:bg-white/20 text-white rounded-xl font-black text-xs uppercase tracking-wider border border-white/20 transition-all flex items-center gap-2 cursor-pointer"
            >
              <LogIn size={16} />
              <span>Register / Sign in</span>
            </button>
          )}
        </div>
      </footer>
    </div>
  );
};
